import {
  Alert,
  Box,
  Button,
  Card,
  Field,
  Flex,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { FC, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { isHttpError, useHttp } from '@mono/http-hook';
import ProfileInfo from '../profile/ProfileInfo';
import useSession from '../session/useSession';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '');

type FormValues = {
  organizationName: string;
  teamName: string;
};

export const OnboardUserPage: FC = () => {
  const session = useSession();
  const [serverError, setServerError] = useState<string | null>(null);
  const http = useHttp();

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: {
      organizationName: '',
      teamName: '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({
    organizationName,
    teamName,
  }) => {
    const organizationSlug = slugify(organizationName);

    if (!organizationSlug) {
      setServerError(
        'Please enter an organization name that contains letters or numbers.'
      );
      return;
    }

    if (!teamName.trim()) {
      setServerError('Please provide a team name to continue.');
      return;
    }

    setServerError(null);

    try {
      await http.post(
        '/api/v1/onboarding',
        {
          organizationName: organizationName.trim(),
          teamName: teamName.trim(),
        },
        { withCredentials: true }
      );

      reset();

      toaster.create({
        type: 'success',
        title: 'Workspace ready',
        description:
          'Your organization and first team are all set. Redirecting you now.',
      });

      await session.update?.();
    } catch (error) {
      let message = 'Unexpected error during onboarding.';

      if (isHttpError(error)) {
        const responseData = error.response?.data;
        if (responseData && typeof responseData === 'object') {
          const { message: responseMessage, error: errorMessage } = responseData as {
            message?: string;
            error?: string;
          };
          message =
            responseMessage ??
            errorMessage ??
            error.message ??
            'Unable to finish onboarding. Please try again.';
        } else {
          message = error.message ?? message;
        }
      } else if (error instanceof Error) {
        message = error.message;
      }

      setServerError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to complete onboarding',
        description: message,
      });
    }
  };

  const welcomeCopy = useMemo(() => {
    const name = session.data?.user?.name?.split(' ')[0];
    if (!name) return 'Welcome to Repacked';
    return `Welcome, ${name}`;
  }, [session.data?.user?.name]);

  return (
    <Flex
      direction="column"
      minH="100vh"
      bg="bg.subtle"
      align="center"
      py={{ base: 10, md: 16 }}
      px={{ base: 4, md: 8 }}
    >
      <Box w="full" maxW="4xl">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap={{ base: 4, md: 6 }}
          mb={{ base: 8, md: 10 }}
        >
          <Box>
            <Text fontSize={{ base: 'sm', md: 'md' }} color="fg.muted">
              Let’s create your first organization and team.
            </Text>
            <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold">
              {welcomeCopy}
            </Text>
          </Box>
          <ProfileInfo />
        </Flex>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Card.Root>
            <Card.Header gap="1">
              <Card.Title>Set up your workspace</Card.Title>
              <Card.Description>
                Share a name for your organization and the first team that will
                join it.
              </Card.Description>
            </Card.Header>
            <Card.Body>
              <Stack gap="4">
                <Field.Root
                  invalid={Boolean(formState.errors.organizationName)}
                >
                  <Field.Label>Organization name</Field.Label>
                  <Input
                    placeholder="Acme Inc."
                    autoComplete="organization"
                    {...register('organizationName', {
                      required: 'Organization name is required.',
                      minLength: {
                        value: 2,
                        message: 'Name must contain at least 2 characters.',
                      },
                    })}
                  />
                  <Field.HelperText>
                    A slug will be generated automatically from the name.
                  </Field.HelperText>
                  <Field.ErrorText>
                    {formState.errors.organizationName?.message}
                  </Field.ErrorText>
                </Field.Root>
                <Field.Root invalid={Boolean(formState.errors.teamName)}>
                  <Field.Label>Team name</Field.Label>
                  <Input
                    placeholder="Design Team"
                    autoComplete="organization"
                    {...register('teamName', {
                      required: 'Team name is required.',
                      minLength: {
                        value: 2,
                        message:
                          'Team name must contain at least 2 characters.',
                      },
                    })}
                  />
                  <Field.HelperText>
                    This will be the default team inside your organization.
                  </Field.HelperText>
                  <Field.ErrorText>
                    {formState.errors.teamName?.message}
                  </Field.ErrorText>
                </Field.Root>
                {serverError ? (
                  <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Description>{serverError}</Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : null}
              </Stack>
            </Card.Body>
            <Card.Footer>
              <Button
                type="submit"
                loading={formState.isSubmitting}
                colorPalette="primary"
              >
                Complete setup
              </Button>
            </Card.Footer>
          </Card.Root>
        </form>
      </Box>
    </Flex>
  );
};
